/**
 * Unit Preferences Settings Component
 * Allows users to configure their preferred units of measurement
 */
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { 
  Ruler, 
  Gauge, 
  RotateCcw, 
  Thermometer, 
  Weight,
  Globe,
  Settings2,
  Info
} from 'lucide-react';
import { useUnitPreferences } from '@/hooks/useUnitPreferences';
import {
  UNIT_SYSTEMS,
  UNIT_DEFINITIONS,
  SpeedUnit,
  DistanceUnit,
  AngleUnit,
  MassUnit,
  TemperatureUnit
} from '@/types/units';
import { toast } from '@/hooks/use-toast';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

const unitPreferencesSchema = z.object({
  speed: z.enum(['mps', 'kmh', 'mph'] as const),
  distance: z.enum(['meters', 'feet', 'yards'] as const),
  angle: z.enum(['degrees', 'radians'] as const),
  mass: z.enum(['kg', 'lbs', 'oz'] as const),
  temperature: z.enum(['celsius', 'fahrenheit', 'kelvin'] as const),
  decimalPlaces: z.object({
    speed: z.number().min(0).max(3),
    distance: z.number().min(0).max(3),
    angle: z.number().min(0).max(3),
    mass: z.number().min(0).max(3),
    temperature: z.number().min(0).max(3),
  }),
});

type UnitPreferencesFormData = z.infer<typeof unitPreferencesSchema>;

export function UnitPreferencesSettings() {
  const { preferences, setPreferences, setUnitSystem, isLoading, reset } = useUnitPreferences();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors: _errors, isSubmitting },
  } = useForm<UnitPreferencesFormData>({
    resolver: zodResolver(unitPreferencesSchema),
    defaultValues: preferences,
    values: preferences, // This ensures form updates when preferences change
  });

  const onSubmit = async (data: UnitPreferencesFormData) => {
    try {
      await setPreferences(data);
      toast({
        title: 'Unit Preferences Updated',
        description: 'Your unit preferences have been saved successfully.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update unit preferences.',
        variant: 'destructive',
      });
    }
  };

  const handleSystemChange = async (systemKey: keyof typeof UNIT_SYSTEMS) => {
    await setUnitSystem(systemKey);
  };

  const handleReset = async () => {
    await reset();
    toast({
      title: 'Preferences Reset',
      description: 'Unit preferences have been reset to defaults.',
    });
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            <span className="ml-2">Loading preferences...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const currentSystem = Object.entries(UNIT_SYSTEMS).find(([_, system]) => 
    system.speed === preferences.speed &&
    system.distance === preferences.distance &&
    system.temperature === preferences.temperature
  )?.[0];

  return (
    <div className="space-y-6">
      {/* Quick System Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Unit System
          </CardTitle>
          <CardDescription>
            Choose a pre-configured unit system or customize individual units below
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(UNIT_SYSTEMS).map(([key, system]) => (
              <Button
                key={key}
                variant={currentSystem === key ? 'default' : 'outline'}
                onClick={() => handleSystemChange(key as keyof typeof UNIT_SYSTEMS)}
                className="h-auto py-3 flex flex-col items-center gap-2"
              >
                <span className="font-medium capitalize">
                  {key.replace('_', ' ')}
                </span>
                <div className="text-xs text-muted-foreground">
                  <div>{UNIT_DEFINITIONS[system.speed]?.symbol}</div>
                  <div>{UNIT_DEFINITIONS[system.distance]?.symbol}</div>
                </div>
              </Button>
            ))}
          </div>
          
          {currentSystem && (
            <Alert className="mt-4">
              <Info className="h-4 w-4" />
              <AlertTitle>Current System: {currentSystem.replace('_', ' ')}</AlertTitle>
              <AlertDescription>
                You're using the {currentSystem.replace('_', ' ')} unit system. 
                Modify individual units below for custom preferences.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Detailed Unit Preferences */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings2 className="h-5 w-5" />
            Custom Unit Preferences
          </CardTitle>
          <CardDescription>
            Fine-tune individual measurement units and precision
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            
            {/* Speed Units */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Gauge className="h-4 w-4" />
                <Label className="text-sm font-medium">Speed</Label>
                <Badge variant="secondary" className="text-xs">
                  Ball & Bat Speed
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-muted-foreground">Unit</Label>
                  <Select 
                    value={watch('speed')} 
                    onValueChange={(value: SpeedUnit) => setValue('speed', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mps">
                        {UNIT_DEFINITIONS.mps.symbol} - {UNIT_DEFINITIONS.mps.name}
                      </SelectItem>
                      <SelectItem value="kmh">
                        {UNIT_DEFINITIONS.kmh.symbol} - {UNIT_DEFINITIONS.kmh.name}
                      </SelectItem>
                      <SelectItem value="mph">
                        {UNIT_DEFINITIONS.mph.symbol} - {UNIT_DEFINITIONS.mph.name}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Decimal Places</Label>
                  <Input
                    type="number"
                    min="0"
                    max="3"
                    {...register('decimalPlaces.speed', { valueAsNumber: true })}
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Distance Units */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Ruler className="h-4 w-4" />
                <Label className="text-sm font-medium">Distance</Label>
                <Badge variant="secondary" className="text-xs">
                  Shot Distance & Height
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-muted-foreground">Unit</Label>
                  <Select 
                    value={watch('distance')} 
                    onValueChange={(value: DistanceUnit) => setValue('distance', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="meters">
                        {UNIT_DEFINITIONS.meters.symbol} - {UNIT_DEFINITIONS.meters.name}
                      </SelectItem>
                      <SelectItem value="feet">
                        {UNIT_DEFINITIONS.feet.symbol} - {UNIT_DEFINITIONS.feet.name}
                      </SelectItem>
                      <SelectItem value="yards">
                        {UNIT_DEFINITIONS.yards.symbol} - {UNIT_DEFINITIONS.yards.name}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Decimal Places</Label>
                  <Input
                    type="number"
                    min="0"
                    max="3"
                    {...register('decimalPlaces.distance', { valueAsNumber: true })}
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Angle Units */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <RotateCcw className="h-4 w-4" />
                <Label className="text-sm font-medium">Angle</Label>
                <Badge variant="secondary" className="text-xs">
                  Launch & Horizontal Angles
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-muted-foreground">Unit</Label>
                  <Select 
                    value={watch('angle')} 
                    onValueChange={(value: AngleUnit) => setValue('angle', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="degrees">
                        {UNIT_DEFINITIONS.degrees.symbol} - {UNIT_DEFINITIONS.degrees.name}
                      </SelectItem>
                      <SelectItem value="radians">
                        {UNIT_DEFINITIONS.radians.symbol} - {UNIT_DEFINITIONS.radians.name}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Decimal Places</Label>
                  <Input
                    type="number"
                    min="0"
                    max="3"
                    {...register('decimalPlaces.angle', { valueAsNumber: true })}
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Mass Units */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Weight className="h-4 w-4" />
                <Label className="text-sm font-medium">Mass</Label>
                <Badge variant="secondary" className="text-xs">
                  Ball & Equipment Weight
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-muted-foreground">Unit</Label>
                  <Select 
                    value={watch('mass')} 
                    onValueChange={(value: MassUnit) => setValue('mass', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="kg">
                        {UNIT_DEFINITIONS.kg.symbol} - {UNIT_DEFINITIONS.kg.name}
                      </SelectItem>
                      <SelectItem value="lbs">
                        {UNIT_DEFINITIONS.lbs.symbol} - {UNIT_DEFINITIONS.lbs.name}
                      </SelectItem>
                      <SelectItem value="oz">
                        {UNIT_DEFINITIONS.oz.symbol} - {UNIT_DEFINITIONS.oz.name}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Decimal Places</Label>
                  <Input
                    type="number"
                    min="0"
                    max="3"
                    {...register('decimalPlaces.mass', { valueAsNumber: true })}
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Temperature Units */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Thermometer className="h-4 w-4" />
                <Label className="text-sm font-medium">Temperature</Label>
                <Badge variant="secondary" className="text-xs">
                  Environmental Conditions
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-muted-foreground">Unit</Label>
                  <Select 
                    value={watch('temperature')} 
                    onValueChange={(value: TemperatureUnit) => setValue('temperature', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="celsius">
                        {UNIT_DEFINITIONS.celsius.symbol} - {UNIT_DEFINITIONS.celsius.name}
                      </SelectItem>
                      <SelectItem value="fahrenheit">
                        {UNIT_DEFINITIONS.fahrenheit.symbol} - {UNIT_DEFINITIONS.fahrenheit.name}
                      </SelectItem>
                      <SelectItem value="kelvin">
                        {UNIT_DEFINITIONS.kelvin.symbol} - {UNIT_DEFINITIONS.kelvin.name}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Decimal Places</Label>
                  <Input
                    type="number"
                    min="0"
                    max="3"
                    {...register('decimalPlaces.temperature', { valueAsNumber: true })}
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button type="submit" disabled={isSubmitting} className="flex-1">
                {isSubmitting ? 'Saving...' : 'Save Preferences'}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleReset}
                className="flex-1"
              >
                Reset to Defaults
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Preview Card */}
      <Card>
        <CardHeader>
          <CardTitle>Preview</CardTitle>
          <CardDescription>
            See how your measurements will be displayed
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <div>
              <Label className="text-muted-foreground">Ball Speed</Label>
              <div className="font-mono">
                42.5{UNIT_DEFINITIONS[watch('speed')]?.symbol}
              </div>
            </div>
            <div>
              <Label className="text-muted-foreground">Distance</Label>
              <div className="font-mono">
                78.3{UNIT_DEFINITIONS[watch('distance')]?.symbol}
              </div>
            </div>
            <div>
              <Label className="text-muted-foreground">Launch Angle</Label>
              <div className="font-mono">
                18.5{UNIT_DEFINITIONS[watch('angle')]?.symbol}
              </div>
            </div>
            <div>
              <Label className="text-muted-foreground">Ball Weight</Label>
              <div className="font-mono">
                163.0{UNIT_DEFINITIONS[watch('mass')]?.symbol}
              </div>
            </div>
            <div>
              <Label className="text-muted-foreground">Temperature</Label>
              <div className="font-mono">
                24.0{UNIT_DEFINITIONS[watch('temperature')]?.symbol}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}